"use client";

import React, { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

type FlowState = 'start' | 'has-approval' | 'no-approval' | 'scope-defined' | 'scope-unclear' | 'prove-value' | 'no-prove-value' | 'result-full-edw' | 'result-quick-assessment-scope' | 'result-embedded' | 'result-quick-assessment-proposal';

export default function InteractiveFlowchart() {
  const [currentState, setCurrentState] = useState<FlowState>('start');

  const resetFlowchart = () => {
    setCurrentState('start');
  };

  const renderContent = () => {
    switch (currentState) {
      case 'start':
        return (
          <div className="max-w-3xl mx-auto text-left md:text-center">
            <div className="bg-white/10 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10 mb-6 sm:mb-8">
              <h3 className="text-white text-[20px] sm:text-2xl md:text-3xl font-serif mb-4 sm:mb-6">
                Do you have board approval for an EDW investment?
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={() => setCurrentState('has-approval')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Yes, we have approval
                </button>
                <button
                  onClick={() => setCurrentState('no-approval')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-white/10 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 active:scale-[0.98]"
                >
                  No, not yet
                </button>
              </div>
            </div>
          </div>
        );

      case 'has-approval':
        return (
          <div className="max-w-3xl mx-auto text-left md:text-center">
            <div className="bg-white/10 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10 mb-6 sm:mb-8">
              <div className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">
                You have approval ✓
              </div>
              <h3 className="text-white text-[20px] sm:text-2xl md:text-3xl font-serif mb-4 sm:mb-6">
                Are scope and budget clearly defined?
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={() => setCurrentState('scope-defined')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Yes, scope is clear
                </button>
                <button
                  onClick={() => setCurrentState('scope-unclear')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-white/10 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 active:scale-[0.98]"
                >
                  No, scope is unclear
                </button>
              </div>
            </div>
          </div>
        );

      case 'no-approval':
        return (
          <div className="max-w-3xl mx-auto text-left md:text-center">
            <div className="bg-white/10 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10 mb-6 sm:mb-8">
              <div className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">
                No approval yet
              </div>
              <h3 className="text-white text-[20px] sm:text-2xl md:text-3xl font-serif mb-4 sm:mb-6">
                Do you need to prove the value of EDW first?
              </h3>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={() => setCurrentState('prove-value')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Yes, need proof of value
                </button>
                <button
                  onClick={() => setCurrentState('no-prove-value')}
                  className="min-h-[48px] px-6 sm:px-8 py-4 bg-white/10 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-300 active:scale-[0.98]"
                >
                  No, need proposal help
                </button>
              </div>
            </div>
          </div>
        );

      case 'scope-defined': case'result-full-edw':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10">
              <div className="text-left md:text-center mb-6 sm:mb-8">
                <div className="inline-block bg-gold text-navy text-xs sm:text-sm font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full uppercase tracking-wider mb-4 sm:mb-6">
                  Recommended Engagement
                </div>
                <h3 className="text-white text-[22px] sm:text-2xl md:text-3xl lg:text-4xl font-serif mb-3 sm:mb-4">
                  Full EDW Implementation
                </h3>
                <p className="text-white/80 text-[15px] sm:text-base md:text-lg mb-6 sm:mb-8">
                  You&apos;re ready for a complete enterprise data warehouse transformation with clear scope and budget.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                <h4 className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">What You&apos;ll Get</h4>
                <ul className="space-y-2 sm:space-y-3 text-white/90 text-[14px] sm:text-base">
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Complete EDW infrastructure tailored to your enterprise needs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>End-to-end ETL pipelines connecting all data sources</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Executive dashboards and role-based analytics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Comprehensive training and documentation</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
                  Schedule Implementation Call
                </button>
              </div>
            </div>
          </div>
        );

      case 'scope-unclear': case'result-quick-assessment-scope':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10">
              <div className="text-left md:text-center mb-6 sm:mb-8">
                <div className="inline-block bg-gold text-navy text-xs sm:text-sm font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full uppercase tracking-wider mb-4 sm:mb-6">
                  Recommended Engagement
                </div>
                <h3 className="text-white text-[22px] sm:text-2xl md:text-3xl lg:text-4xl font-serif mb-3 sm:mb-4">
                  Quick Assessment
                </h3>
                <p className="text-white/80 text-[15px] sm:text-base md:text-lg mb-6 sm:mb-8">
                  Define cost, ROI, and scope before moving forward with full implementation.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                <h4 className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">What You&apos;ll Get</h4>
                <ul className="space-y-2 sm:space-y-3 text-white/90 text-[14px] sm:text-base">
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Comprehensive data landscape audit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>EDW feasibility report with technology recommendations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Detailed ROI projection and cost analysis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Phased implementation roadmap</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
                  Start Assessment
                </button>
              </div>
            </div>
          </div>
        );

      case 'prove-value': case'result-embedded':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10">
              <div className="text-left md:text-center mb-6 sm:mb-8">
                <div className="inline-block bg-gold text-navy text-xs sm:text-sm font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full uppercase tracking-wider mb-4 sm:mb-6">
                  Recommended Engagement
                </div>
                <h3 className="text-white text-[22px] sm:text-2xl md:text-3xl lg:text-4xl font-serif mb-3 sm:mb-4">
                  Embedded Data Scientist
                </h3>
                <p className="text-white/80 text-[15px] sm:text-base md:text-lg mb-6 sm:mb-8">
                  Prove the value of data-driven insights with a dedicated expert embedded in your team.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                <h4 className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">What You&apos;ll Get</h4>
                <ul className="space-y-2 sm:space-y-3 text-white/90 text-[14px] sm:text-base">
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Dedicated data scientist working directly with your team</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Weekly progress reports and stakeholder updates</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Custom analytics solutions addressing immediate needs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Knowledge transfer to build internal capabilities</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
                  Request Embedded Expert
                </button>
              </div>
            </div>
          </div>
        );

      case 'no-prove-value': case'result-quick-assessment-proposal':
        return (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-sm border-2 border-gold rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-10">
              <div className="text-left md:text-center mb-6 sm:mb-8">
                <div className="inline-block bg-gold text-navy text-xs sm:text-sm font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full uppercase tracking-wider mb-4 sm:mb-6">
                  Recommended Engagement
                </div>
                <h3 className="text-white text-[22px] sm:text-2xl md:text-3xl lg:text-4xl font-serif mb-3 sm:mb-4">
                  Quick Assessment
                </h3>
                <p className="text-white/80 text-[15px] sm:text-base md:text-lg mb-6 sm:mb-8">
                  Build a compelling proposal for leadership with clear ROI and implementation roadmap.
                </p>
              </div>

              <div className="bg-white/10 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                <h4 className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-wider mb-3 sm:mb-4">What You&apos;ll Get</h4>
                <ul className="space-y-2 sm:space-y-3 text-white/90 text-[14px] sm:text-base">
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Comprehensive data landscape audit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>EDW feasibility report with technology recommendations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Detailed ROI projection and cost analysis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3 mt-1">◦</span>
                    <span>Phased implementation roadmap</span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button className="min-h-[48px] px-6 sm:px-8 py-4 bg-gradient-to-r from-[#B8956A] to-[#D4AF7A] text-navy font-semibold rounded-lg hover:shadow-[0_4px_16px_rgba(184,149,106,0.4)] transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]">
                  Build Your Proposal
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="bg-navy py-12 sm:py-16 md:py-24 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-left md:text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="font-serif text-white text-[26px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.2] font-normal mb-4 sm:mb-6">
            Interactive <span className="italic">Decision Guide</span>
          </h2>
          <p className="text-white/80 text-[15px] sm:text-base md:text-lg max-w-2xl md:mx-auto mb-6 sm:mb-8">
            Answer a few questions to find your ideal engagement model
          </p>

          {/* Reset Button */}
          {currentState !== 'start' && (
            <button
              onClick={resetFlowchart}
              className="inline-flex items-center justify-center md:justify-center gap-2 w-full sm:w-auto min-h-[44px] px-6 py-3 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-all duration-300 active:scale-[0.98]"
            >
              <ArrowPathIcon className="w-5 h-5" />
              Start Over
            </button>
          )}
        </div>

        {/* Dynamic Flowchart Content */}
        <div className="transition-all duration-500 ease-in-out">
          {renderContent()}
        </div>
      </div>
    </section>
  );
}